import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gauge, RefreshCw, AlertTriangle, Loader2, Landmark, Users, Mail, ShoppingBag, HeartPulse, Pin, ArrowUp, ArrowDown, Tv } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { MetricTile } from '../components/ops/MetricTile';
import { ZoomDialog } from '../components/ops/ZoomDialog';
import { ActionButton } from '../components/ops/ActionButton';
import { WallDisplay } from '../components/ops/WallDisplay';
import { BriefingSettings } from '../components/ops/BriefingSettings';
import { STATUS_COLOURS } from '../components/ops/Dial';

const AREA_ICONS = { money: Landmark, membership: Users, comms: Mail, trade: ShoppingBag, health: HeartPulse };
const PRESET_LABELS = { treasurer: 'Treasurer view', secretary: 'Secretary view' };

function AttentionList({ items, onZoom }) {
  if (!items.length) return <p className="text-sm" style={{ color: STATUS_COLOURS.green }} data-testid="ops-all-clear">All clear — nothing needs attention right now.</p>;
  return (
    <ul className="grid sm:grid-cols-2 gap-2" data-testid="ops-attention">
      {items.map((a) => (
        <li key={a.key} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 bg-white/90" style={{ borderLeft: `4px solid ${STATUS_COLOURS[a.status]}` }} data-testid={`ops-attention-${a.key}`}>
          <button type="button" onClick={() => onZoom(a.key)} className="text-left text-sm hover:underline min-w-0 truncate" style={{ color: 'var(--ink)' }}>
            <span className="font-title" style={{ color: 'var(--navy)' }}>{a.value}{a.unit}</span> {a.label}
          </button>
          <div className="flex gap-1.5 shrink-0">
            {a.actions.filter((x) => x.allowed).slice(0, 1).map((x) => <ActionButton key={x.key} action={x} tone="ghost" />)}
            {a.link && <Link to={a.link} className="text-xs underline whitespace-nowrap" style={{ color: 'var(--navy)' }}>Open</Link>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ViewControls({ prefs, onPreset, onWallSeconds, onWall }) {
  return (
    <div className="relative mt-5 flex flex-wrap items-center gap-2 text-xs" data-testid="ops-view-controls">
      <span className="opacity-70 mr-1">My view:</span>
      {Object.keys(PRESET_LABELS).map((p) => (
        <button key={p} onClick={() => onPreset(p)} className="px-2.5 py-1 rounded-full font-title" style={{ background: 'rgba(255,255,255,0.12)', color: 'var(--cream)' }} data-testid={`ops-preset-${p}`}>{PRESET_LABELS[p]}</button>
      ))}
      <span className="opacity-70 ml-2">Pin tiles with the <Pin size={11} className="inline" /> icon; reorder areas with the arrows.</span>
      <span className="ml-auto flex items-center gap-2">
        <label className="flex items-center gap-1 opacity-80">Wall: <select value={prefs.wall_seconds} onChange={(e) => onWallSeconds(Number(e.target.value))} className="rounded px-1 py-0.5 text-xs" style={{ color: 'var(--navy)' }} data-testid="ops-wall-seconds">
          {[10, 15, 20, 30, 45, 60].map((s) => <option key={s} value={s}>{s}s</option>)}
        </select></label>
        <button onClick={onWall} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-title" style={{ background: 'var(--gold)', color: 'var(--navy)' }} data-testid="ops-wall-start"><Tv size={14} /> Wall display</button>
      </span>
    </div>
  );
}

export default function OpsDashboard() {
  const { http, user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [wall, setWall] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await http.get('/admin/dashboard'); setData(r.data); setErr(''); }
    catch (e) { setErr((e.response && e.response.data && e.response.data.detail) || 'Could not load the dashboard.'); }
    setBusy(false);
  }, [http]);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);

  const savePrefs = async (patch) => {
    try {
      const { data: prefs } = await http.put('/admin/dashboard/prefs', patch);
      setData((d) => ({ ...d, prefs }));
    } catch (e) { toast.error('Could not save your view.'); }
  };
  const applyPreset = async (name) => {
    try {
      const { data: prefs } = await http.post(`/admin/dashboard/prefs/preset/${name}`);
      setData((d) => ({ ...d, prefs })); toast.success(`${PRESET_LABELS[name]} applied.`);
    } catch (e) { toast.error('Could not apply that view.'); }
  };
  const togglePin = (key) => {
    const pinned = data.prefs.pinned.includes(key) ? data.prefs.pinned.filter((k) => k !== key) : [...data.prefs.pinned, key];
    savePrefs({ pinned });
  };

  const byKey = Object.fromEntries((data?.tiles || []).map((t) => [t.key, t]));
  const counts = (data?.tiles || []).reduce((acc, t) => ({ ...acc, [t.status]: (acc[t.status] || 0) + 1 }), {});
  const orderedAreas = data ? [...data.areas].sort((a, b) => {
    const o = data.prefs.area_order; const ia = o.indexOf(a.key), ib = o.indexOf(b.key);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  }) : [];
  const moveArea = (key, dir) => {
    const order = orderedAreas.map((a) => a.key);
    const i = order.indexOf(key), j = i + dir;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    savePrefs({ area_order: order });
  };
  const pinnedTiles = data ? data.prefs.pinned.map((k) => byKey[k]).filter(Boolean) : [];

  return (
    <div data-testid="ops-dashboard">
      <div className="rounded-2xl p-5 md:p-7 mb-6 relative overflow-hidden" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '18px 18px' }} />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Gauge size={30} style={{ color: 'var(--gold)' }} />
              <h1 className="font-title text-3xl md:text-4xl">Operations Dashboard</h1>
            </div>
            <p className="text-sm mt-1 opacity-80" data-testid="ops-greeting">
              {user?.name ? `${user.name} — ` : ''}{data ? `readings taken ${new Date(data.generated_at).toLocaleString()}` : 'loading readings…'}{data?.previous_date ? ` · trends vs ${data.previous_date}` : ''}
            </p>
            <div className="flex gap-3 mt-3 text-xs" data-testid="ops-status-summary">
              {['red', 'amber', 'green'].map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLOURS[s] }} /> {counts[s] || 0} {s}</span>
              ))}
            </div>
          </div>
          <button onClick={load} disabled={busy} className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-title" style={{ background: 'rgba(255,255,255,0.12)', color: 'var(--cream)' }} data-testid="ops-refresh">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
          </button>
        </div>
        {data && (
          <>
            <div className="relative mt-5 flex flex-wrap gap-2" data-testid="ops-quick-actions">
              {data.quick_actions.filter((a) => a.allowed).map((a) => <ActionButton key={a.key} action={a} size="lg" tone="maroon" onDone={() => load()} />)}
            </div>
            <ViewControls prefs={data.prefs} onPreset={applyPreset} onWallSeconds={(s) => savePrefs({ wall_seconds: s })} onWall={() => setWall(true)} />
          </>
        )}
      </div>

      {err && <p className="text-sm mb-4 px-3 py-2 rounded" style={{ background: '#fdecea', color: '#8a1f11' }} data-testid="ops-error">{err}</p>}

      {data && (
        <>
          <section className="rounded-xl p-4 mb-8" style={{ background: 'rgba(201,161,59,0.12)', border: '1px solid rgba(201,161,59,0.4)' }} data-testid="ops-attention-panel">
            <h2 className="font-title text-lg mb-3 flex items-center gap-2" style={{ color: 'var(--maroon)' }}><AlertTriangle size={18} /> Needs attention ({data.attention.length})</h2>
            <AttentionList items={data.attention} onZoom={(k) => setZoom(byKey[k])} />
          </section>

          <BriefingSettings />

          {pinnedTiles.length > 0 && (
            <section className="mb-8" data-testid="ops-pinned">
              <div className="flex items-center gap-2 mb-3">
                <Pin size={18} style={{ color: 'var(--maroon)' }} />
                <h2 className="font-title text-xl" style={{ color: 'var(--navy)' }}>My pinned readings</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {pinnedTiles.map((t) => <MetricTile key={t.key} tile={t} onZoom={setZoom} pinned onPin={togglePin} />)}
              </div>
            </section>
          )}

          {orderedAreas.map((area, idx) => {
            const Icon = AREA_ICONS[area.key] || Gauge;
            const tiles = data.tiles.filter((t) => t.area === area.key);
            return (
              <section key={area.key} className="mb-8" data-testid={`ops-area-${area.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={18} style={{ color: 'var(--maroon)' }} />
                  <h2 className="font-title text-xl" style={{ color: 'var(--navy)' }}>{area.label}</h2>
                  <span className="text-xs" style={{ color: 'var(--olive)' }}>{tiles.filter((t) => t.status !== 'green' && t.status !== 'neutral').length} to look at</span>
                  <span className="ml-auto flex gap-1">
                    <button onClick={() => moveArea(area.key, -1)} disabled={idx === 0} className="p-1 rounded disabled:opacity-20 hover:bg-black/5" title="Move up" data-testid={`ops-area-up-${area.key}`}><ArrowUp size={14} /></button>
                    <button onClick={() => moveArea(area.key, 1)} disabled={idx === orderedAreas.length - 1} className="p-1 rounded disabled:opacity-20 hover:bg-black/5" title="Move down" data-testid={`ops-area-down-${area.key}`}><ArrowDown size={14} /></button>
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {tiles.map((t) => <MetricTile key={t.key} tile={t} onZoom={setZoom} pinned={data.prefs.pinned.includes(t.key)} onPin={togglePin} />)}
                </div>
              </section>
            );
          })}
        </>
      )}
      {zoom && <ZoomDialog tile={byKey[zoom.key] || zoom} onClose={() => setZoom(null)} onRefresh={load} />}
      {wall && data && <WallDisplay data={data} areas={orderedAreas} seconds={data.prefs.wall_seconds || 20} onClose={() => setWall(false)} />}
    </div>
  );
}
